import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Shield, 
  Building2,
  Mail,
  Send,
  Clock,
  UserCheck,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  CheckCircle
} from 'lucide-react';
import { getOrganizationalUnits, submitAccessRequest, getPendingAccessRequests, getApprovedAccessRequests, updateAccessRequestStatus, updateRequestStatusesFromUsers, checkUserExists } from '../firebase';
import { useUser } from '../contexts/UserContext';
import { getUserDomain } from '../utils/domainAccess';

const AccessRequest = () => {
  const [organisationalUnits, setOrganisationalUnits] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [showApprovedList, setShowApprovedList] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [request, setRequest] = useState({
    preferredUsername: '',
    alternativeEmail: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    department: '',
    organisationalUnit: '',
    accessLevel: 'basic',
    reason: '',
    managerEmail: '',
    urgent: false
  });
  
  // Get current user context and domain
  const { user } = useUser();
  const currentUserDomain = user ? getUserDomain(user.email) : null;

  // Helper function to safely format Firestore Timestamps
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      // Handle Firestore Timestamp with toDate() method
      if (dateValue && typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toLocaleString();
      }
      
      // Handle Firestore Timestamp with seconds property
      if (dateValue && typeof dateValue.seconds === 'number') {
        return new Date(dateValue.seconds * 1000).toLocaleString();
      }
      
      // Handle ISO string or regular date
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return 'N/A';
    }
  };

  useEffect(() => {
    fetchOrganizationalUnits();
    fetchPendingRequests();
  }, []);

  const fetchOrganizationalUnits = async () => {
    try {
      const response = await getOrganizationalUnits();
      if (response.data?.success && response.data.data) {
        const ouData = response.data.data.map(ou => ({
          id: ou.orgUnitId,
          path: ou.orgUnitPath,
          name: ou.name
        })).filter(ou => ou.id && ou.path);
        setOrganisationalUnits(ouData);
      }
    } catch (error) {
      console.error('Error fetching organisational units:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      console.log('Fetching access requests...');
      const response = await getPendingAccessRequests();
      console.log('Requests response:', response);
      if (response.data?.success && response.data.data) {
        console.log('Setting requests:', response.data.data);
        setPendingRequests(response.data.data);
      } else {
        console.log('No requests found or response failed');
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const fetchApprovedRequests = async () => {
    if (approvedRequests.length > 0) {
      // Already loaded, just toggle visibility
      setShowApprovedList(!showApprovedList);
      return;
    }

    setLoadingApproved(true);
    try {
      console.log('Fetching approved access requests...');
      const response = await getApprovedAccessRequests();
      console.log('Approved requests response:', response);
      if (response.data?.success && response.data.data) {
        console.log('Setting approved requests:', response.data.data);
        setApprovedRequests(response.data.data);
        setShowApprovedList(true);
      } else {
        console.log('No approved requests found or response failed');
        setApprovedRequests([]);
        setShowApprovedList(true);
      }
    } catch (error) {
      console.error('Error fetching approved requests:', error);
      setApprovedRequests([]);
    } finally {
      setLoadingApproved(false);
    }
  };

  const handleUpdateRequestStatus = async (requestId, newStatus) => {
    try {
      await updateAccessRequestStatus({
        requestId: requestId,
        status: newStatus
      });
      
      // Refresh the pending requests list
      await fetchPendingRequests();
      
      alert(`Request status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating request status:', error);
      alert('Failed to update request status');
    }
  };

  const handleCheckUserStatuses = async () => {
    try {
      console.log('Checking user statuses for pending requests...');
      const result = await updateRequestStatusesFromUsers();
      console.log('Status update result:', result);
      
      if (result.data?.success) {
        alert(`Status update: ${result.data.message}`);
        // Refresh the pending requests list
        await fetchPendingRequests();
      }
    } catch (error) {
      console.error('Error checking user statuses:', error);
      alert(`Failed to check user statuses: ${error.message}`);
    }
  };

  const checkUsernameExists = async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus(null);
      return;
    }

    setCheckingUsername(true);
    try {
      const result = await checkUserExists({ username });
      console.log('Username check result:', result);
      setUsernameStatus(result.data);
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameStatus({
        success: false,
        error: error.message
      });
    } finally {
      setCheckingUsername(false);
    }
  };

  // Debounced username check
  const debouncedCheckUsername = React.useCallback(
    debounce((username) => {
      checkUsernameExists(username);
    }, 1000),
    []
  );

  // Debounce function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Check if user already exists before submitting
      if (usernameStatus?.exists) {
        alert('This username already exists in Google Workspace. Please choose a different username or contact IT if you need access to this existing account.');
        setSubmitting(false);
        return;
      }

      // Auto-append domain if not provided
      let fullEmail = request.preferredUsername;
      if (currentUserDomain && !request.preferredUsername.includes('@')) {
        fullEmail = `${request.preferredUsername}@${currentUserDomain}`;
      }

      const response = await submitAccessRequest({
        ...request,
        preferredUsername: fullEmail,
        requestedBy: user?.email || 'Unknown',
        requestedAt: new Date().toISOString()
      });

      if (response.data?.success) {
        alert('Access request submitted successfully! IT administrators will review your request and contact you soon.');
        setShowRequestModal(false);
        setRequest({
          preferredUsername: '',
          alternativeEmail: '',
          firstName: '',
          lastName: '',
          jobTitle: '',
          department: '',
          organisationalUnit: '',
          accessLevel: 'basic',
          reason: '',
          managerEmail: '',
          urgent: false
        });
        // Refresh the pending requests list
        console.log('Refreshing pending requests after submission...');
        await fetchPendingRequests();
      } else {
        alert('Failed to submit access request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting access request:', error);
      alert('Failed to submit access request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const accessLevels = [
    { 
      value: 'basic', 
      label: 'Basic User', 
      description: 'Standard device access, take screenshots, reboots etc' 
    },
    { 
      value: 'device_admin', 
      label: 'Device Admin Access', 
      description: 'Device administrative privileges for specific OUs' 
    },
    { 
      value: 'client_admin', 
      label: 'Client Admin', 
      description: 'Full administrative access for specific OU\'s' 
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading organisational units...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <UserCheck className="h-8 w-8 text-blue-600 mr-3" />
                Request new team member access
              </h1>
              <p className="mt-2 text-gray-600">
                Request access to Google Admin Console for new team members
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCheckUserStatuses}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors text-sm"
              >
                Check User Status
              </button>
              <button
                onClick={() => setShowRequestModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center transition-colors"
              >
                <Plus className="h-5 w-5 mr-2" />
                Request Access
              </button>
            </div>
          </div>
        </div>

        {/* Information Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Processing Time</h3>
                <p className="text-gray-600">Next Business day</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Mail className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Email Notification</h3>
                <p className="text-gray-600">IT team will contact you</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Secure Process</h3>
                <p className="text-gray-600">Approved by IT administrators</p>
              </div>
            </div>
          </div>
        </div>

        {/* Approved Requests - Collapsible */}
        <div className="bg-white rounded-lg shadow mb-8">
          <button
            onClick={fetchApprovedRequests}
            className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
              Approved Access Requests
              {approvedRequests.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({approvedRequests.length})
                </span>
              )}
            </h2>
            {loadingApproved ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
            ) : (
              showApprovedList ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )
            )}
          </button>
          
          {showApprovedList && approvedRequests.length > 0 && (
            <div className="divide-y divide-gray-200">
              {approvedRequests.map((req) => (
                <div key={req.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {req.firstName} {req.lastName}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          req.status === 'approved' 
                            ? 'bg-green-100 text-green-800'
                            : req.status === 'active'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {req.status === 'approved' ? 'Approved' : 
                           req.status === 'active' ? 'Active' : req.status}
                        </span>
                        {req.urgent && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Urgent
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <p><strong>Email:</strong> {req.preferredUsername}</p>
                          <p><strong>Alternative Email:</strong> {req.alternativeEmail}</p>
                          <p><strong>Job Title:</strong> {req.jobTitle}</p>
                        </div>
                        <div>
                          <p><strong>Department:</strong> {req.department}</p>
                          <p><strong>Organisational Unit:</strong> {req.organisationalUnit}</p>
                          <p><strong>Access Level:</strong> {req.accessLevel}</p>
                        </div>
                      </div>
                      {req.managerEmail && (
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Manager Email:</strong> {req.managerEmail}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 mt-2">
                        <strong>Reason:</strong> {req.reason}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Requested by: {req.requestedBy} • {formatDate(req.requestedAt)}
                        {req.updatedAt && (
                          <span> • Updated: {formatDate(req.updatedAt)}</span>
                        )}
                      </p>
                    </div>
                    <div className="ml-4 text-sm text-green-600">
                      <div className="flex items-center">
                        <UserCheck className="h-4 w-4 mr-1" />
                        {req.status === 'approved' ? 'Approved' : 'Active'}
                      </div>
                      {req.userDetails && (
                        <div className="mt-1 text-xs text-gray-500">
                          <p>OU: {req.userDetails.orgUnitPath}</p>
                          <p>Status: {req.userDetails.suspended ? 'Suspended' : 'Active'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {showApprovedList && approvedRequests.length === 0 && !loadingApproved && (
            <div className="p-6 text-center text-gray-500">
              <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p>No approved requests found</p>
            </div>
          )}
        </div>

        {/* Pending Requests */}
        {pendingRequests.length > 0 && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Clock className="h-6 w-6 text-yellow-600 mr-2" />
                Pending Access Requests ({pendingRequests.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-200">
              {pendingRequests.map((req) => (
                <div key={req.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {req.firstName} {req.lastName}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          req.status === 'approved' 
                            ? 'bg-green-100 text-green-800'
                            : req.status === 'active'
                            ? 'bg-blue-100 text-blue-800'
                            : req.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {req.status === 'approved' ? 'Approved' : 
                           req.status === 'active' ? 'Active' :
                           req.status === 'rejected' ? 'Rejected' : 'Pending'}
                        </span>
                        {req.urgent && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Urgent
                          </span>
                        )}
                        {req.userExists && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            User Exists
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <p><strong>Email:</strong> {req.preferredUsername}</p>
                          <p><strong>Alternative Email:</strong> {req.alternativeEmail}</p>
                          <p><strong>Job Title:</strong> {req.jobTitle}</p>
                        </div>
                        <div>
                          <p><strong>Department:</strong> {req.department}</p>
                          <p><strong>Organisational Unit:</strong> {req.organisationalUnit}</p>
                          <p><strong>Access Level:</strong> {req.accessLevel}</p>
                        </div>
                      </div>
                      {req.managerEmail && (
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Manager Email:</strong> {req.managerEmail}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 mt-2">
                        <strong>Reason:</strong> {req.reason}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Requested by: {req.requestedBy} • {formatDate(req.requestedAt)}
                      </p>
                    </div>
                    {req.status === 'pending' && (
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                          className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-md hover:bg-green-200 transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleUpdateRequestStatus(req.id, 'active')}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-md hover:bg-blue-200 transition-colors"
                        >
                          Mark Active
                        </button>
                        <button
                          onClick={() => handleUpdateRequestStatus(req.id, 'rejected')}
                          className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-md hover:bg-red-200 transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    
                    {req.status === 'approved' && (
                      <div className="ml-4 text-sm text-green-600">
                        <div className="flex items-center">
                          <UserCheck className="h-4 w-4 mr-1" />
                          User account created and approved
                        </div>
                        {req.userDetails && (
                          <div className="mt-1 text-xs text-gray-500">
                            <p>OU: {req.userDetails.orgUnitPath}</p>
                            <p>Status: {req.userDetails.suspended ? 'Suspended' : 'Active'}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {req.status === 'active' && (
                      <div className="ml-4 text-sm text-blue-600">
                        <div className="flex items-center">
                          <UserCheck className="h-4 w-4 mr-1" />
                          User is active
                        </div>
                      </div>
                    )}
                    
                    {req.status === 'rejected' && (
                      <div className="ml-4 text-sm text-red-600">
                        <div className="flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Request rejected
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">How it works</h3>
              <ol className="list-decimal list-inside text-blue-800 space-y-1">
                <li>Fill out the access request form with the user's details</li>
                <li>Specify the required access level and organisational unit</li>
                <li>IT administrators will review and approve the request</li>
                <li>You'll receive an email notification once the user account is created</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Request User Access</h2>
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmitRequest} className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={request.firstName}
                      onChange={(e) => setRequest({ ...request, firstName: e.target.value })}
                      className="input-field"
                      placeholder="John"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={request.lastName}
                      onChange={(e) => setRequest({ ...request, lastName: e.target.value })}
                      className="input-field"
                      placeholder="Doe"
                      required
                    />
                  </div>
                </div>

                {/* Preferred Google Admin Console Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Google Admin Console Username *
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={request.preferredUsername}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRequest({ ...request, preferredUsername: value });
                        // Check username after user stops typing
                        if (currentUserDomain && !value.includes('@')) {
                          debouncedCheckUsername(`${value}@${currentUserDomain}`);
                        } else if (value.includes('@')) {
                          debouncedCheckUsername(value);
                        }
                      }}
                      className={`input-field rounded-r-none ${
                        usernameStatus?.exists 
                          ? 'border-green-500 bg-green-50' 
                          : usernameStatus?.success === false 
                          ? 'border-red-500 bg-red-50' 
                          : ''
                      }`}
                      placeholder="username"
                      required
                    />
                    {currentUserDomain && (
                      <span className="inline-flex items-center px-3 text-sm text-gray-500 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md">
                        @{currentUserDomain}
                      </span>
                    )}
                  </div>
                  
                  {/* Username Status Display */}
                  {checkingUsername && (
                    <div className="flex items-center mt-2 text-sm text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Checking username...
                    </div>
                  )}
                  
                  {usernameStatus && !checkingUsername && (
                    <div className={`mt-2 p-3 rounded-md text-sm ${
                      usernameStatus.exists 
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : usernameStatus.success === false
                        ? 'bg-red-50 border border-red-200 text-red-800'
                        : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                    }`}>
                      {usernameStatus.exists ? (
                        <div>
                          <div className="font-medium flex items-center">
                            <UserCheck className="h-4 w-4 mr-2" />
                            User Already Exists
                          </div>
                          <div className="mt-1 text-xs">
                            <p><strong>Name:</strong> {usernameStatus.user?.name?.fullName || 'N/A'}</p>
                            <p><strong>OU:</strong> {usernameStatus.user?.orgUnitPath || 'N/A'}</p>
                            <p><strong>Status:</strong> {usernameStatus.user?.suspended ? 'Suspended' : 'Active'}</p>
                            {usernameStatus.user?.lastLoginTime && (
                              <p><strong>Last Login:</strong> {new Date(usernameStatus.user.lastLoginTime).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                      ) : usernameStatus.success === false ? (
                        <div className="font-medium flex items-center">
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Error checking username: {usernameStatus.error}
                        </div>
                      ) : (
                        <div className="font-medium flex items-center">
                          <UserCheck className="h-4 w-4 mr-2" />
                          Username available - ready for new account creation
                        </div>
                      )}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-1">This will be the user's Google Workspace email address</p>
                </div>

                {/* Alternative Email Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alternative Email Address (Client Company Email) *
                  </label>
                  <input
                    type="email"
                    value={request.alternativeEmail}
                    onChange={(e) => setRequest({ ...request, alternativeEmail: e.target.value })}
                    className="input-field"
                    placeholder="user@clientcompany.com"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">The user's external email address for notifications</p>
                </div>

                {/* Job Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title *
                    </label>
                    <input
                      type="text"
                      value={request.jobTitle}
                      onChange={(e) => setRequest({ ...request, jobTitle: e.target.value })}
                      className="input-field"
                      placeholder="Software Engineer"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department *
                    </label>
                    <input
                      type="text"
                      value={request.department}
                      onChange={(e) => setRequest({ ...request, department: e.target.value })}
                      className="input-field"
                      placeholder="Engineering"
                      required
                    />
                  </div>
                </div>

                {/* Organisational Unit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organisational Unit *
                  </label>
                  <select
                    value={request.organisationalUnit}
                    onChange={(e) => setRequest({ ...request, organisationalUnit: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Select an organisational unit</option>
                    {organisationalUnits.map(ou => (
                      <option key={ou.id} value={ou.name}>
                        {ou.name} ({ou.path})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Access Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Required Access Level *
                  </label>
                  <div className="space-y-2">
                    {accessLevels.map(level => (
                      <label key={level.value} className="flex items-start">
                        <input
                          type="radio"
                          name="accessLevel"
                          value={level.value}
                          checked={request.accessLevel === level.value}
                          onChange={(e) => setRequest({ ...request, accessLevel: e.target.value })}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{level.label}</div>
                          <div className="text-sm text-gray-600">{level.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Manager Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manager Email Address
                  </label>
                  <input
                    type="email"
                    value={request.managerEmail}
                    onChange={(e) => setRequest({ ...request, managerEmail: e.target.value })}
                    className="input-field"
                    placeholder="manager@company.com"
                  />
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Access *
                  </label>
                  <textarea
                    value={request.reason}
                    onChange={(e) => setRequest({ ...request, reason: e.target.value })}
                    className="input-field"
                    rows={3}
                    placeholder="Please explain why this user needs access to Google Workspace..."
                    required
                  />
                </div>

                {/* Urgent Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="urgent"
                    checked={request.urgent}
                    onChange={(e) => setRequest({ ...request, urgent: e.target.checked })}
                    className="mr-3"
                  />
                  <label htmlFor="urgent" className="text-sm font-medium text-gray-700">
                    This is an urgent request (requires immediate attention)
                  </label>
                </div>

                {/* Submit Buttons */}
                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessRequest;
